import { isPhpNumeric } from '../rolls/dice-roll.js';
import { ecId, ecLean, itemField, record, upper, type JanusReaders, type LogRow, type Row } from '../ability/context.js';
import { emptyActivity, retireActivityStatus, statusScalar, type ActivityPlan, type ActivityWrite } from '../actions/activity.js';
import { casterSkills } from '../pl/passes.js';
import { ftTrapClassification, ftTrapStatusRowEndsSortable } from '../port/k4-dispatch/ft_trap_fire.js';
import { browserDependencies } from '../port/browser-dependencies.js';
import type { ReadEnv } from '../port/k4-dispatch/shared.js';
import { findRow, qtyOf, templateIdOf } from '../minigames/rows.js';
import { usableStacks, type ToolReaders } from '../minigames/lockpick.js';
import { statusItemId } from '../statuses/expiry.js';
import { trapArmed } from './lifecycle.js';
import { mountedTrapAbilityIds } from './mounted.js';
import { trapHostDc } from './fire.js';
import { browserDisarmRandom, mintMechanical, mintRuneBoard, skillEdge, tierFromDc } from './board-mint.js';

export const MACHINING_TOOLS = 'ECE59082-AF18-B7FB-C239-8C3AFBFF49E4';
export const ENCHANTING_TOOLS = '1FBB3F1C-D81C-B925-B295-CC8FEAFAD0B7';
const TRAPPED = '78C51D2D-5178-551C-1249-B5DDA4083098';
export const TIMED_FINISH_SKIP = 'trap-timed-finish:rearm-or-trigger:not-ported';
type Readers = Pick<JanusReaders, 'value' | 'entries' | 'statuses' | 'definition' | 'inCombat' | 'campaignClock' | 'inventoryField' | 'resolveInventory'> & ToolReaders;
export interface DisarmBeginInput {
  host: Row; actor: Row; encounter: Row; read: Readers;
  /** Optional exact row for callers that have already selected a tool. Empty string means no tool. */
  toolInventoryId?: string;
  random?: () => number;
}
export interface DisarmBeginPlan {
  classification: 'mechanical' | 'magical'; skill_var: 'machining' | 'magic'; trap_dc: number;
  trap_tier: number; skill_tier: number; edge: number;
  geometry: ReturnType<typeof mintMechanical> | ReturnType<typeof mintRuneBoard>;
  tool_inventory_id: string; ap: { before: number; after: number } | null;
  status_retire: ActivityWrite[]; rows: LogRow[];
}
export type DisarmBeginDecision = { ok: true; plan: DisarmBeginPlan } | {
  ok: false; error: string; reason?: string; maintenance: ActivityPlan;
};

/** PHP's five tool-proof reasons, using the native inventory readers and the page's carried/floor trees. */
export function disarmToolProof(input: DisarmBeginInput, id: string, template: string): string | null {
  const { actor, encounter, read } = input;
  if (!id) return 'no_tool';
  const carried = findRow([actor.inventory], id), floor = findRow([encounter.encounter_inventories_arr], id);
  const hit = carried ?? floor;
  const row = hit?.row ?? read.resolveInventory?.(upper(id));
  if (!row || Number(read.inventoryField?.(row, 'inventory_deleted') ?? 0) !== 0 || qtyOf(row) <= 0) return 'tool_gone';
  if (templateIdOf(row) !== template) return 'tool_not_routed';
  const holders = hit?.holders ?? [];
  for (const holder of holders) {
    const parent = findRow([actor.inventory, encounter.encounter_inventories_arr], holder)?.row;
    if (parent && read.itemType(parent) === 'digsite') return 'tool_buried';
  }
  if (!hit) return 'tool_not_held';
  return null;
}

/** Only an actually due stored clock produces a retirement and named diagnostic. */
export function trapClockMaintenance(host: Row, actor: Row, read: Pick<Readers, 'campaignClock' | 'statuses'>): ActivityPlan {
  const maintenance = emptyActivity();
  const clock = read.campaignClock();
  const sortable = (parts: number[]): string => parts.map((n, i) => String(Math.trunc(n)).padStart(i === 0 ? 8 : 2, '0')).join('');
  const now = clock ? sortable([clock.year, clock.month, clock.day, clock.hour, clock.minute, clock.second]) : null;
  for (const status of read.statuses(host)) {
    if (statusItemId(status) !== TRAPPED) continue;
    const ends = ftTrapStatusRowEndsSortable(Object.fromEntries(
      ['year', 'month', 'day', 'hour', 'minute', 'second'].map(unit => [`status_ends_campaign_${unit}`, statusScalar(status, `status_ends_campaign_${unit}`)])));
    if (now && ends && ends <= now) {
      retireActivityStatus(maintenance, status);
      // Lean SQL values compare as normalized scalars; empty pointers are omitted by the compile.
      const op = maintenance.writes[maintenance.writes.length - 1]!;
      for (const key of ['status_target_id', 'status_caster_id', 'status_connected_id']) op.before![key] = String(statusScalar(status, key) ?? '');
    }
  }
  if (maintenance.writes.length) {
    const name = String(ecLean(actor, 'encounter_creature_name') ?? itemField(actor, 'item_name') ?? 'Unnamed creature');
    maintenance.rows.push({ roll_type_1: 'janus_unported_skip', roll_type_2: null, roll_item_id: null,
      roll_by: ecId(actor), roll_to: null, roll_result: 0, roll_private: 1, rolls_full: `${name}: skipped ${TIMED_FINISH_SKIP}` });
  }
  return maintenance;
}

/** Begin order: lazy clock → armed → routing/tools → DC/skill tiers → combat AP → browser mint. No live dice on refusal. */
export function planDisarmBegin(input: DisarmBeginInput): DisarmBeginDecision {
  const { host, actor, encounter, read } = input;
  const maintenance = trapClockMaintenance(host, actor, read);
  const refuse = (error: string, reason?: string): DisarmBeginDecision => ({ ok: false, error, ...(reason ? { reason } : {}), maintenance });
  const retired = new Set(maintenance.writes.map(op => String(op.id)));
  if (!trapArmed(host, row => read.statuses(row).filter(status => !retired.has(String(statusScalar(status, 'status_id')))))) return refuse('not_armed');
  const mounted = mountedTrapAbilityIds(host, read.entries, read.definition);
  const payload = mounted[0] ? read.definition(mounted[0]) : {};
  // The adapter hydrates a mounted definition before planning. This is missing page state, never a server hand-back.
  if (!payload) return refuse('host_gone');
  const classification = ftTrapClassification(payload, browserDependencies({ layer_value: (row: unknown, key: unknown) => read.value(record(row), String(key)) }) as unknown as ReadEnv) as 'mechanical' | 'magical';
  const template = classification === 'magical' ? ENCHANTING_TOOLS : MACHINING_TOOLS;
  const stacks = usableStacks(actor, encounter, template, read);
  const candidate = input.toolInventoryId ?? stacks.rows.find(candidate => disarmToolProof(input, candidate.inventoryId, template) === null)?.inventoryId ?? '';
  const reason = disarmToolProof(input, candidate, template);
  if (reason) return refuse(!candidate && stacks.outOfReach ? 'tools_out_of_reach' : 'missing_tools', reason);
  const floor = findRow([encounter.encounter_inventories_arr], candidate);
  if (floor && !findRow([actor.inventory], candidate) && read.reachable(floor.holders[0] ?? candidate) !== true) return refuse('tools_out_of_reach');
  const dc = read.inventoryField?.(host, 'inventory_inspection_dc');
  const trap_dc = trapHostDc({ inventory: { inventory_inspection_dc: isPhpNumeric(dc) ? dc : null } });
  const trap_tier = tierFromDc(trap_dc), skill_var = classification === 'magical' ? 'magic' : 'machining';
  const skill_tier = Math.max(0, Math.trunc(Number(record(casterSkills(actor)[skill_var])?.tier_level ?? 0)));
  const edge = skillEdge(skill_tier, trap_tier);
  const before = Number(ecLean(actor, 'aps') ?? 0);
  const ap = read.inCombat(encounter, actor) ? { before, after: before - 3 } : null;
  if (ap && before < 3) return refuse('not_enough_aps');
  const random = input.random ?? browserDisarmRandom();
  const geometry = classification === 'magical' ? mintRuneBoard(trap_tier, edge, random) : mintMechanical(trap_tier, edge, random);
  return { ok: true, plan: { classification, skill_var, trap_dc, trap_tier, skill_tier, edge, geometry,
    tool_inventory_id: candidate, ap, status_retire: maintenance.writes, rows: maintenance.rows } };
}
